import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EProviderEndPoint, providersApi } from "../services/providersApi";
import {
  ICategoryInterfaces,
  ICountUnit,
  IClient,
  IColor,
  ICountry,
  ICsbCountry,
  ILspCategory,
  ILspPower,
  IMerchandiser,
  IProviderResponse,
  ISales,
  IStandard,
  IStandardInterfaces,
  IStyleCategory,
  IStyleUnit,
  ITcCompany,
  IYarnComposition,
  IYarnCount,
} from "../modules/orders/models/order";
import { checkTokenValidity } from "../modules/user/services/AuthProvider";
import { IPaymentTerm, ISupplierType } from "../modules/raw-material/models/rmm_models";

const providerQueryOptions = {
  staleTime: 30 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

// get all supplier type
export const useSupplierTypeProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<ISupplierType>
  >([EProviderEndPoint.SUPPLIER_TYPE]);

  const query = useQuery<IProviderResponse<ISupplierType>, Error>({
    queryKey: [EProviderEndPoint.SUPPLIER_TYPE],
    queryFn: () => providersApi.getSupplierType(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all client
export const useClientProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IClient>>([
    EProviderEndPoint.CLIENT,
  ]);

  const query = useQuery<IProviderResponse<IClient>, Error>({
    queryKey: [EProviderEndPoint.CLIENT],
    queryFn: () => providersApi.getClients(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all merchandiser
export const useMerchandiserProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IMerchandiser>
  >([EProviderEndPoint.MERCHANDISER]);

  const query = useQuery<IProviderResponse<IMerchandiser>, Error>({
    queryKey: [EProviderEndPoint.MERCHANDISER],
    queryFn: () => providersApi.getMerchandiser(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn composition
export const useYarnCompositionProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IYarnComposition>
  >([EProviderEndPoint.YARN_COMPOSITION]);

  const query = useQuery<IProviderResponse<IYarnComposition>, Error>({
    queryKey: [EProviderEndPoint.YARN_COMPOSITION],
    queryFn: () => providersApi.getYarnComposition(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp, refetch: query.refetch, isFetching: query.isFetching };
};

// get all yarn count
export const useYarnCountProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IYarnCount>>([
    EProviderEndPoint.YARN_COUNT,
  ]);

  const query = useQuery<IProviderResponse<IYarnCount>, Error>({
    queryKey: [EProviderEndPoint.YARN_COUNT],
    queryFn: () => providersApi.getYarnCount(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp, refetch: query.refetch, isFetching: query.isFetching };
};

export const useCountUnitProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ICountUnit>>([
    EProviderEndPoint.COUNT_UNIT,
  ]);

  const query = useQuery<IProviderResponse<ICountUnit>, Error>({
    queryKey: [EProviderEndPoint.COUNT_UNIT],
    queryFn: () => providersApi.getCountUnit(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return {
    data,
    success,
    timestamp,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};

// get all sales
export const useSalesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ISales>>([
    EProviderEndPoint.SALES,
  ]);

  const query = useQuery<IProviderResponse<ISales>, Error>({
    queryKey: [EProviderEndPoint.SALES],
    queryFn: () => providersApi.getSales(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useContractSuffixProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.CONTRACT_SUFFIX]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.CONTRACT_SUFFIX],
    queryFn: () => providersApi.getContractSuffix(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useTcCompanyProvider = (enabled:boolean=true) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ITcCompany>>([
    EProviderEndPoint.TC_COMPANY,
  ]);

  const query = useQuery<IProviderResponse<ITcCompany>, Error>({
    queryKey: [EProviderEndPoint.TC_COMPANY],
    queryFn: () => providersApi.getTcCompany(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: enabled && checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { ...query, data, success, timestamp };
};

// get all yarn count
export const useCompaniesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.COMPANIES]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.COMPANIES],
    queryFn: () => providersApi.getCompanies(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const usePaymentTermProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IPaymentTerm>
  >([EProviderEndPoint.PAYMENT_TERM]);

  const query = useQuery<IProviderResponse<IPaymentTerm>, Error>({
    queryKey: [EProviderEndPoint.PAYMENT_TERM],
    queryFn: () => providersApi.getPaymentTerm(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useCurrenciesProvider = (enabled:boolean=true) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.CURRENCIES]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.CURRENCIES],
    queryFn: () => providersApi.getCurrencies(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: enabled && checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useCountryOriginProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ICountry>>([
    EProviderEndPoint.COUNTRY_ORIGIN,
  ]);

  const query = useQuery<IProviderResponse<ICountry>, Error>({
    queryKey: [EProviderEndPoint.COUNTRY_ORIGIN],
    queryFn: () => providersApi.getOrigin(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useMachinesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.MACHINES]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.MACHINES],
    queryFn: () => providersApi.getMachines(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useDyeTypesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.DYE_TYPES,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.DYE_TYPES],
    queryFn: () => providersApi.getDyeTypes(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useCategoriesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<ICategoryInterfaces>
  >([EProviderEndPoint.CATEGORIES]);

  const query = useQuery<IProviderResponse<ICategoryInterfaces>, Error>({
    queryKey: [EProviderEndPoint.CATEGORIES],
    queryFn: () => providersApi.getCategories(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useProductsProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<ICategoryInterfaces>
  >([EProviderEndPoint.PRODUCTS]);

  const query = useQuery<IProviderResponse<ICategoryInterfaces>, Error>({
    queryKey: [EProviderEndPoint.PRODUCTS],
    queryFn: () => providersApi.getProducts(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useFactoriesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.FACTORIES]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.FACTORIES],
    queryFn: () => providersApi.getFactories(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useShipmentTermProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.SHIPMENT_TERM]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.SHIPMENT_TERM],
    queryFn: () => providersApi.getShipmentTerm(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

// get all yarn count
export const useGaugesProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStandardInterfaces>
  >([EProviderEndPoint.GAUGES]);

  const query = useQuery<IProviderResponse<IStandardInterfaces>, Error>({
    queryKey: [EProviderEndPoint.GAUGES],
    queryFn: () => providersApi.getGauges(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useStichProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.STITCHES,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.STITCHES],
    queryFn: () => providersApi.getStiches(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useCollarProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.COLLARS,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.COLLARS],
    queryFn: () => providersApi.getCollars(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useShoulderProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.SHOULDERS,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.SHOULDERS],
    queryFn: () => providersApi.getShoulders(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useSleeveProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.SLEEVES,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.SLEEVES],
    queryFn: () => providersApi.getSleeves(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const usePocketProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStandard>>([
    EProviderEndPoint.POCKETS,
  ]);

  const query = useQuery<IProviderResponse<IStandard>, Error>({
    queryKey: [EProviderEndPoint.POCKETS],
    queryFn: () => providersApi.getPockets(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useCsbCountryProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ICsbCountry>>(
    [EProviderEndPoint.CSB_COUNTRY]
  );

  const query = useQuery<IProviderResponse<ICsbCountry>, Error>({
    queryKey: [EProviderEndPoint.CSB_COUNTRY],
    queryFn: () => providersApi.getCsbCountry(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useColorsProvider = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IColor>>([
    EProviderEndPoint.COLORS,
  ]);

  const query = useQuery<IProviderResponse<IColor>, Error>({
    queryKey: [EProviderEndPoint.COLORS],
    queryFn: () => providersApi.getColors(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: enabled && checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useAccessoryCategoryProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<IStyleCategory>
  >([EProviderEndPoint.STYLE_ACCESSORY_CATEGORY]);

  const query = useQuery<IProviderResponse<IStyleCategory>, Error>({
    queryKey: [EProviderEndPoint.STYLE_ACCESSORY_CATEGORY],
    queryFn: () => providersApi.getStyleAccCategory(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useAccessoryUnitProvider = (enabled:boolean=true) => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<IStyleUnit>>([
    EProviderEndPoint.STYLE_ACCESSORY_UNIT,
  ]);

  const query = useQuery<IProviderResponse<IStyleUnit>, Error>({
    queryKey: [EProviderEndPoint.STYLE_ACCESSORY_UNIT],
    queryFn: () => providersApi.getStyleAccUnit(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: enabled && checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useLspPowerProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<IProviderResponse<ILspPower>>([
    EProviderEndPoint.LSP_POWER,
  ]);

  const query = useQuery<IProviderResponse<ILspPower>, Error>({
    queryKey: [EProviderEndPoint.LSP_POWER],
    queryFn: () => providersApi.getLspPower(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};

export const useLspCategoryProvider = () => {
  const queryClient = useQueryClient();
  const previousData = queryClient.getQueryData<
    IProviderResponse<ILspCategory>
  >([EProviderEndPoint.LSP_CATEGORY]);

  const query = useQuery<IProviderResponse<ILspCategory>, Error>({
    queryKey: [EProviderEndPoint.LSP_CATEGORY],
    queryFn: () => providersApi.getLspCategory(),
    placeholderData: previousData,
    ...providerQueryOptions,
    enabled: checkTokenValidity(),
  });

  const { data, success, timestamp } = query.data || {};

  return { data, success, timestamp };
};
